import {
	Badge,
	Button,
	Card,
	Divider,
	Flex,
	Group,
	Modal,
	SimpleGrid,
	Stack,
	Text,
	TextInput,
	Textarea,
	Title,
} from '@mantine/core'
import { IconCheck, IconRefresh, IconSearch, IconX } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	getRedeemRequests,
	getRedeemRequestStats,
	markRedeemRequestPaid,
	rejectRedeemRequest,
} from 'src/apis/redeemRequestAPI'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import TableComponent from 'src/components/TableComponent'
import actionClasses from 'src/components/TableComponent/TableComponent.module.scss'
import { useModal } from 'src/context/ModalContext'
import { color, formatDateTimeMinus } from 'src/variable/constant'

type RedeemStatus = 'PENDING' | 'PAID' | 'REJECTED'

interface RedeemRow {
	id: string
	no: number
	points: number
	amount_vnd: number
	status: RedeemStatus
	created_at?: string
	processed_at?: string
	bank_name?: string
	account_holder_name?: string
	account_number?: string
	email?: string
	phone?: string
	rejection_reason?: string
	processed_by_employee_id?: string
	user?: { id?: string; name?: string; email?: string; phone?: string; avatar?: string }
}

const STATUS_OPTIONS = [
	{ value: '', label: 'All statuses' },
	{ value: 'PENDING', label: 'Pending' },
	{ value: 'PAID', label: 'Paid' },
	{ value: 'REJECTED', label: 'Rejected' },
]

const formatMoney = (value?: number) => `${Number(value || 0).toLocaleString('en-US')} VND`
const formatDate = (value?: string) => (value && dayjs(value).isValid() ? dayjs(value).format(formatDateTimeMinus) : '-')
const getRowsAndCount = (response: any) => {
	const objects = response?.data?.results?.objects
	const rows = Array.isArray(objects?.rows) ? objects.rows : []
	return { rows, count: Number(objects?.count ?? response?.data?.pagination?.total ?? rows.length) }
}

const statusColor: Record<RedeemStatus, string> = {
	PENDING: 'yellow',
	PAID: 'green',
	REJECTED: 'red',
}

function RedeemRequestsPage() {
	const { openError, openModal } = useModal()
	const [rows, setRows] = useState<RedeemRow[]>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)
	const [search, setSearch] = useState('')
	const [status, setStatus] = useState('')
	const [from, setFrom] = useState('')
	const [to, setTo] = useState('')
	const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 1, pageSize: 20 })
	const [stats, setStats] = useState<Record<RedeemStatus, { count: number; amount_vnd: number }>>({
		PENDING: { count: 0, amount_vnd: 0 }, PAID: { count: 0, amount_vnd: 0 }, REJECTED: { count: 0, amount_vnd: 0 },
	})
	const [detail, setDetail] = useState<RedeemRow | null>(null)
	const [processing, setProcessing] = useState<{ row: RedeemRow; action: 'paid' | 'reject' } | null>(null)
	const [rejectionReason, setRejectionReason] = useState('')

	const queryParams = useMemo(() => ({
		offset: (pagination.pageIndex - 1) * pagination.pageSize,
		limit: pagination.pageSize,
		q: search.trim() || undefined,
		status: status || undefined,
		from: from || undefined,
		to: to || undefined,
	}), [from, pagination, search, status, to])

	const fetchData = useCallback(async () => {
		setLoading(true)
		try {
			const [listResponse, statsResponse] = await Promise.all([
				getRedeemRequests(queryParams),
				getRedeemRequestStats({ from: from || undefined, to: to || undefined }),
			])
			const result = getRowsAndCount(listResponse)
			setTotal(result.count)
			setRows(result.rows.map((item: RedeemRow, index: number) => ({
				...item,
				no: index + 1 + (pagination.pageIndex - 1) * pagination.pageSize,
				points: Number(item.points || 0), amount_vnd: Number(item.amount_vnd || 0),
			})))
			const resultStats = statsResponse?.data?.results?.object || {}
			setStats({
				PENDING: resultStats.PENDING || { count: 0, amount_vnd: 0 },
				PAID: resultStats.PAID || { count: 0, amount_vnd: 0 },
				REJECTED: resultStats.REJECTED || { count: 0, amount_vnd: 0 },
			})
		} catch (error) { openError(error) } finally { setLoading(false) }
	}, [from, openError, pagination.pageIndex, pagination.pageSize, queryParams, to])

	useEffect(() => { fetchData() }, [fetchData])
	useEffect(() => { setPagination((previous) => ({ ...previous, pageIndex: 1 })) }, [search, status, from, to])

	const completeProcess = useCallback(async () => {
		if (!processing) return
		if (processing.action === 'reject' && !rejectionReason.trim()) return
		setActionLoadingId(processing.row.id)
		try {
			if (processing.action === 'paid') await markRedeemRequestPaid(processing.row.id)
			else await rejectRedeemRequest(processing.row.id, rejectionReason.trim())
			setProcessing(null); setRejectionReason('')
			openModal({ type: 'success', label: processing.action === 'paid' ? 'Redeem request marked as paid' : 'Redeem request rejected' })
			await fetchData()
		} catch (error) { openError(error) } finally { setActionLoadingId(null) }
	}, [fetchData, openError, openModal, processing, rejectionReason])

	const columns = useMemo<MRT_ColumnDef<RedeemRow>[]>(() => [
		{ accessorKey: 'no', header: '#', size: 50 },
		{ accessorKey: 'id', header: 'Request ID', size: 220, Cell: ({ cell }) => <Text size="xs">{String(cell.getValue() || '-')}</Text> },
		{ accessorKey: 'user', header: 'User', size: 220, Cell: ({ row }) => <Stack spacing={1}><Text size="sm" fw={600}>{row.original.user?.name || '-'}</Text><Text size="xs" color="dimmed">{row.original.email || row.original.user?.email || '-'}</Text><Text size="xs" color="dimmed">{row.original.phone || row.original.user?.phone || '-'}</Text></Stack> },
		{ accessorKey: 'points', header: 'Points', size: 90 },
		{ accessorKey: 'amount_vnd', header: 'Amount', size: 130, Cell: ({ cell }) => formatMoney(Number(cell.getValue())) },
		{ accessorKey: 'status', header: 'Status', size: 105, Cell: ({ cell }) => { const value = cell.getValue<RedeemStatus>(); return <Badge color={statusColor[value] || 'gray'}>{value}</Badge> } },
		{ accessorKey: 'created_at', header: 'Created', size: 145, Cell: ({ cell }) => formatDate(String(cell.getValue() || '')) },
		{ accessorKey: 'processed_at', header: 'Processed', size: 145, Cell: ({ cell }) => formatDate(String(cell.getValue() || '')) },
	], [])

	const renderActions = useCallback((row: any) => {
		const item = row.original as RedeemRow
		if (item.status !== 'PENDING') return null
		const disabled = Boolean(actionLoadingId)
		return <Flex gap={6}>
			<CustomTooltip withinPortal label="Mark as paid"><button type="button" disabled={disabled} className={`${actionClasses.actionButton} ${actionClasses.primaryAction}`} onClick={() => setProcessing({ row: item, action: 'paid' })}><IconCheck size={16} /></button></CustomTooltip>
			<CustomTooltip withinPortal label="Reject"><button type="button" disabled={disabled} className={`${actionClasses.actionButton} ${actionClasses.dangerAction}`} onClick={() => { setRejectionReason(''); setProcessing({ row: item, action: 'reject' }) }}><IconX size={16} /></button></CustomTooltip>
		</Flex>
	}, [actionLoadingId])

	const totalCount = stats.PENDING.count + stats.PAID.count + stats.REJECTED.count
	const totalAmount = stats.PENDING.amount_vnd + stats.PAID.amount_vnd + stats.REJECTED.amount_vnd
	const cards = [
		{ label: 'Total requests', value: totalCount, amount: totalAmount, color: '#1f6feb' },
		{ label: 'Pending', value: stats.PENDING.count, amount: stats.PENDING.amount_vnd, color: '#b7791f' },
		{ label: 'Paid', value: stats.PAID.count, amount: stats.PAID.amount_vnd, color: color.CmsPrimary },
		{ label: 'Rejected', value: stats.REJECTED.count, amount: stats.REJECTED.amount_vnd, color: '#c53030' },
	]

	return <Stack spacing={16} sx={{ width: '100%' }}>
		<Flex justify="space-between" align="center" wrap="wrap" gap={12}><div><Title order={2}>Redeem requests</Title><Text color="dimmed" size="sm">Review point-to-cash payout requests and bank-account snapshots.</Text></div><Button leftIcon={<IconRefresh size={16} />} variant="outline" onClick={fetchData} loading={loading}>Refresh</Button></Flex>
		<SimpleGrid cols={4} breakpoints={[{ maxWidth: 'md', cols: 2 }, { maxWidth: 'xs', cols: 1 }]}>{cards.map((card) => <Card key={card.label} withBorder radius="md" p="md"><Text size="sm" color="dimmed">{card.label}</Text><Text fw={700} size="xl" sx={{ color: card.color }}>{card.value.toLocaleString('en-US')}</Text><Text size="xs" color="dimmed">{formatMoney(card.amount)}</Text></Card>)}</SimpleGrid>
		<Card withBorder radius="md" p="md"><Flex gap={10} align="flex-end" wrap="wrap"><TextInput label="Search" placeholder="Request ID, name, email or phone" value={search} onChange={(event) => setSearch(event.currentTarget.value)} icon={<IconSearch size={16} />} sx={{ flex: 1, minWidth: 250 }} /><CustomSelect label="Status" value={status} onChange={(value) => setStatus(value || '')} data={STATUS_OPTIONS} clearable withinPortal zIndex={1000} /><TextInput label="From" type="datetime-local" value={from} onChange={(event) => setFrom(event.currentTarget.value)} /><TextInput label="To" type="datetime-local" value={to} onChange={(event) => setTo(event.currentTarget.value)} /></Flex></Card>
		<TableComponent<RedeemRow> total={total} data={rows} loading={loading} pagination={pagination} columns={columns} handleChangePage={(pageIndex) => setPagination((previous) => ({ ...previous, pageIndex }))} handleChangePageSize={(pageSize) => setPagination({ pageIndex: 1, pageSize })} handleDetail={(row) => setDetail(row.original)} detailActionLabel="View details" detailActionIcon="eye" _renderCustomActions={renderActions} hideMoreActions lockActionColumn actionColumnSize={144} />
		<Modal opened={Boolean(detail)} onClose={() => setDetail(null)} title="Redeem request details" size="lg">{detail && <Stack spacing="sm"><Group grow><Text><b>User:</b> {detail.user?.name || '-'}</Text><Text><b>User email:</b> {detail.user?.email || '-'}</Text></Group><Text><b>User phone:</b> {detail.user?.phone || '-'}</Text><Divider /><Text><b>Bank:</b> {detail.bank_name}</Text><Text><b>Account holder:</b> {detail.account_holder_name}</Text><Text><b>Account number:</b> {detail.account_number}</Text><Text><b>Bank email:</b> {detail.email || '-'}</Text><Text><b>Bank phone:</b> {detail.phone || '-'}</Text><Divider /><Group grow><Text><b>Points:</b> {detail.points.toLocaleString('en-US')}</Text><Text><b>Amount:</b> {formatMoney(detail.amount_vnd)}</Text></Group><Text><b>Status:</b> <Badge color={statusColor[detail.status]}>{detail.status}</Badge></Text><Text><b>Created:</b> {formatDate(detail.created_at)}</Text><Text><b>Processed:</b> {formatDate(detail.processed_at)}</Text>{detail.processed_by_employee_id && <Text><b>Processed by:</b> {detail.processed_by_employee_id}</Text>}{detail.rejection_reason && <Text color="red"><b>Rejection reason:</b> {detail.rejection_reason}</Text>}</Stack>}</Modal>
		<Modal opened={Boolean(processing)} onClose={() => { if (!actionLoadingId) setProcessing(null) }} title={processing?.action === 'paid' ? 'Mark request as paid' : 'Reject redeem request'}>{processing && <Stack><Text>{processing.action === 'paid' ? `Confirm payment of ${formatMoney(processing.row.amount_vnd)} to ${processing.row.account_holder_name}?` : 'Provide a reason. Points will be returned to the user once.'}</Text>{processing.action === 'reject' && <Textarea required label="Rejection reason" value={rejectionReason} onChange={(event) => setRejectionReason(event.currentTarget.value)} error={!rejectionReason.trim() ? 'Required' : undefined} />}<Group position="right"><Button variant="default" onClick={() => setProcessing(null)} disabled={Boolean(actionLoadingId)}>Cancel</Button><Button color={processing.action === 'paid' ? 'green' : 'red'} onClick={completeProcess} loading={Boolean(actionLoadingId)} disabled={processing.action === 'reject' && !rejectionReason.trim()}>{processing.action === 'paid' ? 'Mark as paid' : 'Reject'}</Button></Group></Stack>}</Modal>
	</Stack>
}

export default RedeemRequestsPage
